import { useCallback, useState } from "react";

import { fetchQuestion } from "./api";
import { useAvaContext } from "./AvaContext";
import { useAvaMessageHandlers } from "./AvaMessageHandlersProvider";
import { useTrpcContext } from "./AvaTRPCContext";
import { type WorkflowsEnum } from "./types";

export type AnswerExtras = {
  answerId: string;
  done?: boolean;
};

export type UseAvaType = {
  answer: string;
  answerExtras: AnswerExtras | undefined;
  askQuestion: ({
    conversationId,
    question,
    conversationCallback,
    flow,
  }: {
    conversationId: string | undefined;
    question: string;
    conversationCallback: (conversationId: string) => void;
    flow?: WorkflowsEnum;
  }) => Promise<void>;
  onStopGeneration: (conversationId: string, answerId: string, partialAnswer: string) => void;
  overrideLongerWait: boolean;
  resetAnswer: () => void;
  avaChatLoading: string | undefined;
};

let answerIdStopped: string | undefined;

export function useAva(
  onAnswerGenerated: () => void,
  clientName = "console",
  abortStatusCallback: () => boolean
): UseAvaType {
  const { fetcher } = useAvaContext();
  const { stopGeneration } = useTrpcContext();
  const [answer, setAnswer] = useState<string>("");
  const [answerExtras, setAnswerExtras] = useState<AnswerExtras | undefined>();
  const [overrideLongerWait, setOverrideLongerWait] = useState<boolean>(false);
  const { getHandler: getMessageHandlers } = useAvaMessageHandlers();
  const [avaChatLoading, setAvaChatLoading] = useState<string | undefined>();

  const resetAnswer = useCallback(() => {
    setAnswer("");
    setAnswerExtras(undefined);
  }, []);

  const askQuestion = useCallback(
    async ({
      conversationId,
      question,
      conversationCallback,
      flow,
    }: {
      conversationId: string | undefined;
      question: string;
      conversationCallback: (conversationId: string) => void;
      flow?: WorkflowsEnum;
    }) => {
      resetAnswer();

      const answerCallback = (chunk: string, answerId?: string) => {
        let shouldStop = false;
        if (answerId && answerIdStopped !== answerId) {
          setAnswerExtras({
            answerId,
          });
        }

        if (!answerId || answerIdStopped !== answerId) {
          if (chunk.includes(JSON.stringify({ run_report_from_config: "processing" }))) {
            setOverrideLongerWait(true);
            return false;
          }
          if (chunk.includes("reportConfig") || chunk.includes("reportResult")) {
            shouldStop = false;
          }
          if (chunk.includes(JSON.stringify({ run_report_from_config: "abort" }))) {
            shouldStop = true;
          }

          if (chunk.includes("toolStart")) {
            setAvaChatLoading(JSON.parse(chunk).toolStart);
            return false;
          }

          if (chunk.includes("llmStart")) {
            setAvaChatLoading(JSON.parse(chunk).value);
            return false;
          }

          if (chunk.includes("llmEnd")) {
            setAvaChatLoading(undefined);
            return false;
          }

          setOverrideLongerWait(false); // only change this if it's already true from a previous question to prevent un-necessary re-renders downstream.

          setAnswer((answer) => answer + chunk);
        }

        return shouldStop;
      };

      const result = await fetchQuestion({
        fetcher,
        question,
        answerCallback,
        conversationCallback,
        conversationId,
        clientName,
        abortStatusCallback,
        getMessageHandlers,
        flow,
      });

      onAnswerGenerated();
      setAnswerExtras(result);
    },
    [fetcher, abortStatusCallback, onAnswerGenerated, clientName, getMessageHandlers, resetAnswer]
  );
  const onStopGeneration = (conversationId: string, answerId: string, partialMessage: string) => {
    answerIdStopped = answerId;
    setOverrideLongerWait(false);
    if (!partialMessage) {
      setAnswer("Message generation stopped");
    }
    stopGeneration({ conversationId, answerId, partialMessage });
  };

  return { answer, answerExtras, askQuestion, onStopGeneration, overrideLongerWait, resetAnswer, avaChatLoading };
}
